import { mergeProps, mergeRefs } from "@react-aria/utils";
import { clsx } from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import {
  type CSSProperties,
  forwardRef,
  type ForwardRefRenderFunction,
  type HTMLAttributes,
  type PropsWithChildren,
  type Ref,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import {
  type AriaPositionProps,
  type AriaTooltipProps,
  OverlayContainer,
  type PositionAria,
  useOverlayPosition,
  useTooltip,
} from "react-aria";
import type { TooltipTriggerState } from "react-stately";

import { Shortcut } from "../Shortcut/Shortcut.tsx";
import { TooltipIntent } from "./settings.ts";
import style from "./Tooltip.module.css";

export interface TooltipApi {
  updatePosition: PositionAria["updatePosition"];
}

export type TooltipProps = PropsWithChildren<
  AriaTooltipProps &
    Omit<AriaPositionProps, "overlayRef"> & {
      title?: string;
      shortcut?: string;
      intent?: TooltipIntent;
      state: TooltipTriggerState;
      className?: string;
      style?: CSSProperties;
      domProps: HTMLAttributes<Element>;
      apiRef?: Ref<TooltipApi>;
      nonInteractive?: boolean;
    }
>;

const AnimatedTooltipRender: ForwardRefRenderFunction<HTMLDivElement, TooltipProps> = (
  { state, children, targetRef, domProps, apiRef, nonInteractive, ...props },
  ref,
) => {
  const overlayRef = useRef(null);
  const { tooltipProps } = useTooltip(props, state);
  const { overlayProps, arrowProps, placement, updatePosition } = useOverlayPosition({
    ...props,
    placement: props.placement ?? "top",
    targetRef,
    overlayRef,
    offset: props.offset ?? 3,
    isOpen: state.isOpen,
  });

  const arrowStyle = useMemo(
    () => ({
      ...arrowProps.style,
      top: placement === "top" ? "100%" : placement === "bottom" ? 0 : arrowProps.style?.top,
      left: placement === "left" ? "100%" : placement === "right" ? 0 : arrowProps.style?.left,
    }),
    [arrowProps, placement],
  );

  useImperativeHandle(
    apiRef,
    () => ({
      updatePosition,
    }),
    [updatePosition],
  );

  return (
    <AnimatePresence>
      {!!state.isOpen && (
        <OverlayContainer key="tooltip" style={nonInteractive ? { pointerEvents: "none" } : undefined}>
          <div
            ref={mergeRefs(ref, overlayRef) as Ref<HTMLDivElement>}
            role="tooltip"
            {...mergeProps(props, tooltipProps, overlayProps, domProps)}
            title={""}
          >
            <motion.div
              initial={{
                opacity: 0,
                x: placement === "left" ? 3 : placement === "right" ? -3 : 0,
                y: placement === "top" ? 3 : placement === "bottom" ? -3 : 0,
              }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{
                opacity: 0,
                x: placement === "left" ? -3 : placement === "right" ? 3 : 0,
                y: placement === "top" ? -3 : placement === "bottom" ? 3 : 0,
              }}
              className={clsx(style.tooltip, {
                [style.light]: props.intent == null || props.intent === TooltipIntent.Default,
                [style.info]: props.intent === TooltipIntent.Info,
                [style.warning]: props.intent === TooltipIntent.Warning,
                [style.danger]: props.intent === TooltipIntent.Danger,
              })}
              data-testid={"tooltip"}
              style={props.style}
            >
              {!!props.title && (
                <div className={style.header}>
                  <div className={style.title}>{props.title}</div>
                  {!!props.shortcut && <Shortcut label={props.shortcut} />}
                </div>
              )}
              <div className={style.with_shortcut}>
                <div className={style.content}>{children}</div>
                {!props.title && !!props.shortcut && <Shortcut label={props.shortcut} />}
              </div>
              <div
                className={style.arrow}
                {...arrowProps}
                style={arrowStyle}
                data-testid={"tooltip-arrow"}
                data-placement={placement}
              />
            </motion.div>
          </div>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
};

export const AnimatedTooltip = forwardRef(AnimatedTooltipRender);
